import ApplicationController from "./application_controller"
import GvToast from "../lib/gv_toast"

// Action helper for the organization opportunities-listing "Pending Review" section
// (opportunities/givsum3/_pending_review_row_*_v3.html.haml). Two entry points, same
// underlying fetch/toast flow:
//   - open() (click->...#open): for plain PUT actions with no form of their own (Waitlist,
//     host Confirm, Approve/Deny, Contribution/Donation Confirm). Immediate mode
//     (data-...-immediate-value="true") fires straight away; otherwise shows the shared
//     confirm dialog (_pending_review_confirm_modal_v3.html.haml) first.
//   - submitForm() (submit->...#submitForm): for the Refute forms (real textarea input),
//     rendered inside a modal-ajax fragment -- intercepts the native submit so it gets the
//     same fetch/toast treatment instead of a full-page navigation.
// On success the row is removed from the DOM and the "N Items Pending Review" count is
// updated in place -- no page reload.
export default class extends ApplicationController {
  // This app's "stimulus" package (2.0.0, pre-@hotwired rename) only supports the bare
  // shorthand form (name: String) for static values -- the { type, default } object form
  // throws "Unknown value type constant" during registration (parseValueTypeConstant gets
  // the whole object instead of a type constant). That throw aborts Application#load()'s
  // registration loop entirely, silently unregistering every controller alphabetically
  // after this file. Defaults are applied manually in code below instead.
  static values = {
    message: String,
    url: String,
    method: String,
    immediate: Boolean,
    successMessage: String
  }

  open(event) {
    event.preventDefault()
    const row = this.element.closest(".pending-review-row")

    if (this.immediateValue) {
      this.submit(this.urlValue, this.methodValue, null, { row })
      return
    }

    const modalEl = document.getElementById("pending-review-confirm-modal-v3")
    const messageEl = document.getElementById("pending-review-confirm-modal-v3-message")
    const submitBtn = document.getElementById("pending-review-confirm-modal-v3-submit")
    messageEl.textContent = this.messageValue

    const handleSubmit = () => {
      instance.hide()
      this.submit(this.urlValue, this.methodValue, null, { row })
      submitBtn.removeEventListener("click", handleSubmit)
    }
    submitBtn.addEventListener("click", handleSubmit)

    const instance = bootstrap.Modal.getOrCreateInstance(modalEl)
    instance.show()
  }

  // Refute forms (contributions/givsum3/confirm_refute_v3, donations/givsum3/confirm_refute_v3)
  // carry data-controller/data-action directly on the <form> tag, plus data-row-id since the
  // form is fetched into a modal appended to <body> -- disconnected from the row that opened it.
  submitForm(event) {
    event.preventDefault()
    const form = event.target
    const row = form.dataset.rowId ? document.getElementById(form.dataset.rowId) : null
    const modal = form.closest(".modal")
    this.submit(form.action, form.method, new FormData(form), { row, modal })
  }

  async submit(url, method, body, { row, modal } = {}) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // redirect: "manual" -- these actions redirect_to on success (e.g. back to the org
      // dashboard). Letting fetch() follow that redirect can fail client-side (a redirect
      // crossing to a different host/custom domain isn't a CORS-safe follow) even though the
      // request itself already mutated the record server-side before the redirect was ever
      // sent. An opaque redirect response IS the success case here, not an error. Accept:
      // text/html pins format negotiation to the controller's format.html branch (the one
      // that redirects) so it can't accidentally land on format.json instead.
      const response = await fetch(url, {
        method: method || "put",
        redirect: "manual",
        body,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": token,
          "Accept": "text/html"
        }
      })
      const succeeded = response.ok || response.type === "opaqueredirect"
      if (!succeeded) throw new Error(`HTTP Error: ${response.status}`)
      GvToast.success(this.successMessageValue || "Done.")
      if (modal) bootstrap.Modal.getInstance(modal)?.hide()
      this.removeRow(row)
    } catch (error) {
      GvToast.error("Something went wrong — please try again.")
    }
  }

  removeRow(row) {
    if (!row) return
    row.remove()
    this.updateCount()
  }

  updateCount() {
    const list = document.getElementById("pending-review-list")
    const countText = document.getElementById("pending-review-count-v3-text")
    if (!list || !countText) return

    const remaining = list.querySelectorAll(".pending-review-row").length
    if (remaining === 0) {
      document.querySelector(".pending-review-card")?.remove()
      return
    }
    countText.textContent = `Pending Approvals (${remaining})`
  }
}
